<template>
  <div
    class="input-container date-range-picker-container"
    :class="{ danger: isDanger, 'time-picker': enableTimePicker }">
    <VueDatePicker
      :id="id"
      v-model="range"
      class="date-range-picker"
      :range="true"
      :multi-calendars="useMultipleCalendars"
      :enable-time-picker="false"
      :first-day-of-week="firstDayOfTheWeek"
      :placeholder="placeholderInternal"
      :locale="getLanguage()"
      :auto-apply="true"
      :clearable="allowClearing"
      @input="fireRangeChanged"
      @formatted-value="fireRangeChanged" />
  </div>
</template>

<script setup lang="ts">
  import VueDatePicker from '@vuepic/vue-datepicker';
  import { DateTime } from 'luxon';
  import { capitalize, computed, onBeforeMount, onMounted, Ref, ref, watch } from 'vue';

  import { getLanguage } from '@/globals';
  import languages from '@/lang';
  import i18n from '@/vueI18n';

  interface IProps {
    id?: string;
    start: DateTime;
    end: DateTime;
    isDanger?: boolean;
    placeholder?: string;
    allowClearing?: boolean;
    enableTimePicker?: boolean;
    useMultipleCalendars?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    isDanger: false,
    placeholder: null,
    allowClearing: false,
    enableTimePicker: false,
    useMultipleCalendars: true,
  });

  const emits = defineEmits(['dateRangeChanged']);

  const textInput = ref(null);

  const placeholderInternal = ref(null);
  const startInternal = ref(DateTime.local().startOf('day'));
  const endInternal = ref(DateTime.local().startOf('day').plus({ day: 1 }));
  const range: Ref<Array<string | DateTime>> = ref([startInternal.value.toISODate(), endInternal.value?.toISODate()]);
  const lastRange: Ref<{ start: DateTime; end: DateTime }> = ref(null);

  let fireRangeChangedTimer = null;

  const isEmpty = computed(() => props.start === null || props.end === null);
  const firstDayOfTheWeek = computed(() => {
    const appLanguage = getLanguage();
    return languages.find((x) => x.value === appLanguage).firstDayOfTheWeek;
  });

  watch(props.start, (val) => {
    if (startInternal.value !== val) {
      startInternal.value = val;
    }
  });

  watch(props.end, (val) => {
    if (endInternal.value !== val) {
      endInternal.value = val;
    }
  });

  watch(startInternal, () => {
    if (range.value && startInternal.value) {
      range.value[0] = startInternal.value.startOf('day').toISODate();
    }
    fireRangeChanged();
  });

  watch(endInternal, () => {
    if (range.value && endInternal.value) {
      range.value[1] = endInternal.value.endOf('day').toISODate();
    }
    fireRangeChanged();
  });

  watch(
    range,
    () => {
      fireRangeChanged();
    },
    { deep: true },
  );

  onBeforeMount(() => {
    startInternal.value = props.start;
    endInternal.value = props.end;
  });

  onMounted(() => {
    placeholderInternal.value =
      props.placeholder === null ? capitalize(i18n.global.t('messages.selectDates')) : props.placeholder;
  });

  function fireRangeChanged() {
    if (fireRangeChangedTimer != null) {
      clearTimeout(fireRangeChangedTimer);
    }
    fireRangeChangedTimer = setTimeout(() => {
      fireRangeChangedTimer = null;
      const start = (
        range.value[0] instanceof Date
          ? DateTime.fromJSDate(range.value[0])
          : DateTime.fromISO(range.value[0] as string)
      ).startOf('day');
      const end = (
        range.value[1] instanceof Date
          ? DateTime.fromJSDate(range.value[1])
          : DateTime.fromISO(range.value[1] as string)
      ).endOf('day');
      const localRange = {
        start,
        end,
      };
      if (
        lastRange.value?.start &&
        lastRange.value?.end &&
        localRange.start != null &&
        localRange.end != null &&
        localRange.start.equals(lastRange.value.start) &&
        localRange.end.equals(lastRange.value.end)
      ) {
        return;
      }
      emits('dateRangeChanged', localRange);
      lastRange.value = localRange;
    }, 100);
  }
</script>

<style lang="scss" rel="stylesheet/scss">
  @use 'sass:color';
  @use '@/styles/datepicker';
  @use '@/styles/variables' as *;

  #app {
    .date-range-picker-container {
      .datepicker {
        width: 30rem !important;
        min-width: 30rem !important;
        box-shadow: $shadow;
      }

      .date-range-picker {
        width: 23rem !important;
      }

      .datetimepicker {
        width: auto;
        right: 0;
      }
    }
  }
</style>
